import {
  TransactionSource,
  TransactionStatus
} from '~/__generated__/backend/zeus';
import { DealershipScope, scopeKey } from '../scope';

/**
 * Inputs that identify a transaction-list fetch. Passed as an object so React
 * Query hashes it stably and order-independently — and `undefined` fields drop
 * out of the hash, so optional filters normalise for free (no manual
 * stringify). Normalise dates to ISO strings before keying.
 */
export type TransactionListFilters = {
  search?: string;
  status?: TransactionStatus[];
  source?: TransactionSource[];
  assignedStaffIds?: string[];
  createdFromIso?: string;
  createdToIso?: string;
  page?: number;
};

/**
 * The single source of truth for transaction cache keys. Sub-resources hang
 * under `detail(txId)`, so invalidating `detail` cascades (prefix match) to
 * todos / documents / compliance / credit reports / vehicle, while a narrower
 * builder stays surgical.
 */
export const transactionKeys = {
  root: (s: DealershipScope) => [...scopeKey.ds(s), 'transaction'] as const,

  lists: (s: DealershipScope) => [...transactionKeys.root(s), 'list'] as const,
  list: (s: DealershipScope, filters: TransactionListFilters) =>
    [...transactionKeys.lists(s), filters] as const,

  detail: (s: DealershipScope, txId: string) =>
    [...transactionKeys.root(s), 'detail', txId] as const,

  // --- sub-resources: all hang under detail(txId) ---
  todos: (s: DealershipScope, txId: string, params: { page?: number } = {}) =>
    [...transactionKeys.detail(s, txId), 'todos', params] as const,
  dealJacket: (s: DealershipScope, txId: string) =>
    [...transactionKeys.detail(s, txId), 'deal-jacket'] as const,
  // A form staff build for this deal, see the deal jacket form builder
  form: (s: DealershipScope, txId: string, formId: string) =>
    [...transactionKeys.detail(s, txId), 'form', formId] as const,
  // The CARFAX report of the purchase or the trade vehicle on the transaction
  // vin is part of the key, editing it asks CARFAX about a different car
  carfaxReport: (s: DealershipScope, txId: string, vin: string) =>
    [...transactionKeys.detail(s, txId), 'carfax-report', vin] as const,
  document: (s: DealershipScope, txId: string, documentId: string) =>
    [...transactionKeys.detail(s, txId), 'document', documentId] as const,
  documentLabels: (s: DealershipScope, txId: string, documentId: string) =>
    [...transactionKeys.document(s, txId, documentId), 'labels'] as const,
  formSubmission: (s: DealershipScope, txId: string, submissionId: string) =>
    [
      ...transactionKeys.detail(s, txId),
      'form-submission',
      submissionId
    ] as const,
  compliance: (s: DealershipScope, txId: string, customerId: string) =>
    [...transactionKeys.detail(s, txId), 'compliance', customerId] as const,
  creditReport: (
    s: DealershipScope,
    txId: string,
    customerId: string,
    reportId: string
  ) =>
    [
      ...transactionKeys.detail(s, txId),
      'credit-report',
      customerId,
      reportId
    ] as const,
  lendingPortal: (s: DealershipScope, txId: string, customerId: string) =>
    [...transactionKeys.detail(s, txId), 'lending-portal', customerId] as const,
  vehicle: (s: DealershipScope, txId: string) =>
    [...transactionKeys.detail(s, txId), 'vehicle'] as const
};
